import React, { FC, useEffect } from "react";
import {
  ProductAttributeInputModel,
  ProductAttributeMappingsInput,
  PurchaseCheckOutDetails,
  getPriceByBillingType,
  PurchaseExtractedAttributeModel,
  PurchaseExtractedAttributeOptionModel,
  PurchaseExtendedAttributeWithOptionModel,
} from "../../shared/models";
import MinusIcon from "../../../../../images/products/minus-blue.svg";
import Plus from "../../../../../images/products/plus.svg";
import { observer } from "mobx-react";
import {
  CustomLabel,
  FlexContainer,
  Input,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { AttributeValueTypes } from "../../shared/enums";
import { SelectedColorButton } from "../../shared/styles";
import {
  CarousalWithThumbnail,
  CustomPricingInput,
  PurchasePriceDescriptionRow,
} from "../../shared/components";
import { sanitizeHtml } from "../../shared/utils/sanitizeHtml";

interface IProps {
  // item: CustomerFacingCheckOutItemModel;
  // updateOptionsPageData: (data: OptionSectionModel) => void;
  // optionsPageData: OptionSectionModel;
  purchaseOutDetails: PurchaseCheckOutDetails;
  onUpdatePurchaseDetails: (data: PurchaseCheckOutDetails) => void;
}

const PurchaseOptions: FC<IProps> = ({
  purchaseOutDetails,
  onUpdatePurchaseDetails,
}) => {
  const [showErrorMessage, setShowErrorMessage] = React.useState("");

  useEffect(() => {
    setShowErrorMessage("");
  }, [purchaseOutDetails.item, purchaseOutDetails.activeItemIndex]);

  // A variant set with exactly one selectable option offers no real choice, so select it
  // automatically instead of blocking the buyer on a mandatory "SELECT ONE".
  useEffect(() => {
    if (!purchaseOutDetails?.item) return;

    // Mirrors the availability rules used when a buyer clicks an option: inventory-tracked
    // options need stock, and a purchase link may restrict which variants are on offer.
    const singleSelectableIndex = (
      variantData: ProductAttributeInputModel | undefined,
      isTrackInventory: boolean,
      onlyAllowedVariantIds: string[]
    ): number => {
      const selectable = (variantData?.options || [])
        .map((option, index) => ({ option, index }))
        .filter(
          ({ option }) =>
            (onlyAllowedVariantIds.length === 0 ||
              onlyAllowedVariantIds.includes(option.id)) &&
            (!isTrackInventory ||
              (!!option.onHand && parseFloat(option.onHand) > 0))
        );
      return selectable.length === 1 ? selectable[0].index : -1;
    };

    const activeItemIndex = purchaseOutDetails.activeItemIndex;

    if (activeItemIndex === -1) {
      const item = purchaseOutDetails.item;
      if (!item.hasVariants || item.selectedVariantIndex !== -1) return;

      const index = singleSelectableIndex(item.variants, item.isTrackInventory, []);
      if (index === -1) return;

      const cloned = new PurchaseCheckOutDetails({ ...purchaseOutDetails });
      cloned.item.selectedVariantIndex = index;
      onUpdatePurchaseDetails(cloned);
      return;
    }

    const activeAddOn = purchaseOutDetails.addOns?.[activeItemIndex];
    if (!activeAddOn?.hasVariants || !activeAddOn.selectedVariant) return;

    const currentIndex = activeAddOn.variantIndex();
    if (currentIndex !== undefined && currentIndex > -1) return;

    const index = singleSelectableIndex(
      activeAddOn.selectedVariant,
      activeAddOn.isTrackInventory,
      activeAddOn.onlyAllowedVariantIds
    );
    if (index === -1) return;

    const cloned = new PurchaseCheckOutDetails({ ...purchaseOutDetails });
    cloned.addOns[activeItemIndex].variantId =
      activeAddOn.selectedVariant.options[index].id;
    onUpdatePurchaseDetails(cloned);
  }, [purchaseOutDetails]);

  if (!purchaseOutDetails.item) return null;

  const { medias, variants } = purchaseOutDetails.item;

  function onQuantityChange(increment: boolean, quantity: number) {
    if (increment) {
      quantity++;
    } else {
      quantity--;
    }

    const clonedPurchaseDetails = new PurchaseCheckOutDetails({
      ...purchaseOutDetails,
    });

    const newQuantity = quantity < 1 ? 1 : quantity;

    const activeItemIndex = clonedPurchaseDetails.activeItemIndex;
    if (activeItemIndex === -1) {
      if (
        clonedPurchaseDetails.item.inventorySettings &&
        clonedPurchaseDetails.item.inventorySettings.isLimitPurchaseQuantity &&
        newQuantity > clonedPurchaseDetails.item.inventorySettings.quantityLimit
      ) {
        setShowErrorMessage(
          `Only ${clonedPurchaseDetails.item.inventorySettings.quantityLimit} items are allowed per purchase.`
        );
        return;
      }
      clonedPurchaseDetails.item.quantity = newQuantity;
    } else {
      const activeAddOn = clonedPurchaseDetails.addOns[activeItemIndex];
      // if (
      //   activeAddOn.inventorySettings &&
      //   activeAddOn.inventorySettings.isLimitPurchaseQuantity &&
      //   newQuantity > activeAddOn.inventorySettings.quantityLimit
      // ) {
      //   setShowErrorMessage(
      //     `Only ${activeAddOn.inventorySettings.quantityLimit} items are allowed per purchase.`
      //   );
      //   return;
      // }
      activeAddOn.quantity = quantity;
    }

    onUpdatePurchaseDetails(clonedPurchaseDetails);
  }

  function onUpdateCustomPricing(value: string) {
    const clonedPurchaseDetails = new PurchaseCheckOutDetails({
      ...purchaseOutDetails,
    });

    const activeItemIndex = clonedPurchaseDetails.activeItemIndex;
    if (activeItemIndex === -1) {
      clonedPurchaseDetails.item.customPricingInputValue = value;
    } else {
      const activeAddOn = clonedPurchaseDetails.addOns[activeItemIndex];
      activeAddOn.customPricingInputValue = value;
    }

    onUpdatePurchaseDetails(clonedPurchaseDetails);
  }

  function reducedPriceFrom() {
    if (purchaseOutDetails.activeItemIndex === -1) {
      if (
        purchaseOutDetails.item.hasVariants &&
        purchaseOutDetails.item.selectedVariantIndex === -1
      )
        return null;
      const priceDescription = getPriceByBillingType(
        purchaseOutDetails.item.itemType,
        purchaseOutDetails.item.hasVariants,
        purchaseOutDetails.item.variants,
        purchaseOutDetails.item.price.id,
        purchaseOutDetails.item.price.billingType,
        purchaseOutDetails.item.price.terms,
        purchaseOutDetails.item.price.recurrence,
        purchaseOutDetails.item.quantity,
        purchaseOutDetails.item.selectedVariantIndex
      );

      if (!priceDescription) return null;

      if (
        priceDescription.originalPriceInNumber &&
        priceDescription.originalPriceInNumber > 0
      ) {
        return (
          <CustomLabel className="price-special-info">
            Reduced from {priceDescription.originalPrice}
          </CustomLabel>
        );
      }
    } else {
      const addOn =
        purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex];

      const index = addOn.variantIndex();
      if (index === -1 || index == undefined) return null;

      const addOnPrice = getPriceByBillingType(
        addOn.itemType,
        addOn.hasVariants,
        addOn.selectedVariant || new ProductAttributeInputModel(),
        addOn.priceId,
        addOn.billingType,
        addOn.terms,
        addOn.recurrence,
        addOn.quantity,
        index
      );
      if (
        addOnPrice?.originalPriceInNumber &&
        addOnPrice.originalPriceInNumber > 0
      ) {
        return (
          <CustomLabel className="price-special-info">
            Reduced from {addOnPrice.originalPrice}
          </CustomLabel>
        );
      }
    }
  }

  function extractVariantOptions(
    variants: ProductAttributeInputModel,
    onlyAllowedVariantIds?: string[]
  ) {
    let list: PurchaseExtendedAttributeWithOptionModel[] = [];
    const data: PurchaseExtendedAttributeWithOptionModel[] = [];
    variants.options.forEach((x) => {
      if (onlyAllowedVariantIds && onlyAllowedVariantIds.length > 0) {
        if (!onlyAllowedVariantIds.includes(x.id)) return;
      }
      // list = list.concat(x.attributeMappings);
      list = list.concat(
        x.attributeMappings.map(
          (a) =>
            new PurchaseExtendedAttributeWithOptionModel({
              variantId: x.id,
              ...a,
            })
        )
      );
    });

    variants.options.forEach((option) => {
      option.attributeMappings.forEach((amp) => {
        const filteredList = list.filter(
          (x) =>
            x.attributeId === amp.attributeId && x.optionId === amp.optionId
        );

        filteredList.forEach((x) => {
          const isExists =
            data.findIndex(
              (f) =>
                f.attributeId === x.attributeId && f.optionId === x.optionId
            ) > -1;
          if (!isExists) {
            data.push(x);
          }
        });
      });
    });

    const filteredData: PurchaseExtractedAttributeModel[] = [];
    data.forEach((x) => {
      const matchedVariant = filteredData.find(
        (f) => f.attributeId === x.attributeId
      );
      if (matchedVariant) {
        const isOptionExists =
          matchedVariant.options.find((f) => f.optionId === x.optionId) !==
          undefined;
        if (isOptionExists) return;

        matchedVariant.options.push(
          new PurchaseExtractedAttributeOptionModel({
            optionId: x.optionId,
            optionName: x.optionName,
            color: x.color,
            variantId: x.variantId,
          })
        );
      } else {
        filteredData.push(
          new PurchaseExtractedAttributeModel({
            variantId: x.variantId,
            attributeId: x.attributeId,
            attributeName: x.attributeName || x.type.toString(),
            attributeValueType: x.valueType,
            options: [
              new PurchaseExtractedAttributeOptionModel({
                optionId: x.optionId,
                optionName: x.optionName,
                color: x.color,
                variantId: x.variantId,
              }),
            ],
          })
        );
      }
    });
    return filteredData;
  }

  function onVariantChange(
    activeVariant: ProductAttributeInputModel,
    attributeId: string,
    optionId: string,
    optionName: string,
    isTrackInventory: boolean,
    selectedVariantIndex: number,
    isJusCheckIfVariantAllowed?: boolean,
    onlyAllowedVariantIds?: string[]
  ) {
    const clonedPurchaseDetails = new PurchaseCheckOutDetails({
      ...purchaseOutDetails,
    });

    const clonedVariant = new ProductAttributeInputModel(activeVariant);

    //FINDNEW VARIANT =
    const selectedVariant = clonedVariant.options[selectedVariantIndex]
      ? new ProductAttributeMappingsInput(
          clonedVariant.options[selectedVariantIndex]
        )
      : null;
    if (selectedVariant) {
      const matchedAttribute = selectedVariant.attributeMappings.find(
        (f) => f.attributeId === attributeId
      );
      if (!matchedAttribute) return false;

      matchedAttribute.optionId = optionId;
      matchedAttribute.optionName = optionName;

      let newVariantIndex = clonedVariant.options.findIndex(
        (x) =>
          (!isTrackInventory ||
            (isTrackInventory && x.onHand && parseFloat(x.onHand) > 0)) &&
          x.attributeMappings.every((y) =>
            selectedVariant.attributeMappings.find(
              (z) =>
                z.attributeId === y.attributeId && z.optionId === y.optionId
            )
          )
      );

      //EG. IF variants are as small, medium, red, mehroon
      //and only combinations are allowed as small-red, medium-mehroon
      //then if user selects small, mehroon, then it should not allow
      if (newVariantIndex === -1) {
        if (!isJusCheckIfVariantAllowed) {
          //SELECT THE COMBINATION CHOOSE AVAILALBE ONE
          newVariantIndex = clonedVariant.options.findIndex(
            (x) =>
              (!isTrackInventory ||
                (isTrackInventory && x.onHand && parseFloat(x.onHand) > 0)) &&
              x.attributeMappings.find(
                (y) => y.attributeId === attributeId && y.optionId === optionId
              )
          );
        }
      }

      if (newVariantIndex === -1) return false;
      const activeItemIndex = clonedPurchaseDetails.activeItemIndex;
      if (activeItemIndex === -1) {
        clonedPurchaseDetails.item.selectedVariantIndex = newVariantIndex;
      } else {
        const activeAddOn = clonedPurchaseDetails.addOns[activeItemIndex];
        activeAddOn.variantId = clonedVariant.options[newVariantIndex].id;
      }

      if (!isJusCheckIfVariantAllowed) {
        onUpdatePurchaseDetails(clonedPurchaseDetails);
      }
      return true;
    } else {
      // const newVariantIndex = clonedVariant.options.findIndex((x) =>
      //   x.attributeMappings.find(
      //     (y) => y.attributeId === attributeId && y.optionId === optionId
      //   )
      // );

      const newVariantIndex = clonedVariant.getVariantIndex(
        isTrackInventory,
        attributeId,
        optionId,
        onlyAllowedVariantIds
      );
      if (newVariantIndex === -1) return false;

      const activeItemIndex = clonedPurchaseDetails.activeItemIndex;
      if (activeItemIndex === -1) {
        clonedPurchaseDetails.item.selectedVariantIndex = newVariantIndex;
      } else {
        const activeAddOn = clonedPurchaseDetails.addOns[activeItemIndex];
        activeAddOn.variantId = clonedVariant.options[newVariantIndex].id;
      }

      if (!isJusCheckIfVariantAllowed) {
        onUpdatePurchaseDetails(clonedPurchaseDetails);
      }
      return true;
    }
  }

  // function isVariantSelectionAllowed(
  //   activeVariant: ProductAttributeInputModel,
  //   attributeId: string,
  //   optionId: string,
  //   selectedVariantIndex: number
  // ): boolean {
  //   const clonedVariant = new ProductAttributeInputModel(activeVariant);

  //   const selectedVariant = clonedVariant.options[selectedVariantIndex]
  //     ? new ProductAttributeMappingsInput(
  //         clonedVariant.options[selectedVariantIndex]
  //       )
  //     : null;
  //   if (selectedVariant) {
  //     const matchedAttribute = selectedVariant.attributeMappings.find(
  //       (f) => f.attributeId === attributeId
  //     );
  //     if (!matchedAttribute) return false;

  //     const newVariantIndex = clonedVariant.options.findIndex((x) =>
  //       x.attributeMappings.every((y) =>
  //         y.attributeId === attributeId
  //           ? y.optionId === optionId
  //           : selectedVariant.attributeMappings.some(
  //               (z) =>
  //                 z.attributeId === y.attributeId && z.optionId === y.optionId
  //             )
  //       )
  //     );

  //     return newVariantIndex !== -1;
  //   }
  //   return false;
  // }

  function isVariantSelectionAllowed(
    activeVariant: ProductAttributeInputModel,
    attributeId: string,
    optionId: string,
    optionName: string,
    isTrackInventory: boolean,
    selectedVariantIndex: number,
    onlyAllowedVariantIds?: string[]
  ): boolean {
    const clonedVariant = new ProductAttributeInputModel(activeVariant);

    const selectedVariant = clonedVariant.options[selectedVariantIndex]
      ? new ProductAttributeMappingsInput(
          clonedVariant.options[selectedVariantIndex]
        )
      : null;

    if (selectedVariant) {
      const matchedAttribute = selectedVariant.attributeMappings.find(
        (f) => f.attributeId === attributeId
      );

      if (!matchedAttribute) return false;
      let newVariantIndex = clonedVariant.options.findIndex(
        (x) =>
          (!isTrackInventory ||
            (isTrackInventory && x.onHand && parseFloat(x.onHand) > 0)) &&
          x.attributeMappings.find(
            (y) => y.attributeId === attributeId && y.optionId === optionId
          )
      );

      return newVariantIndex === -1 ? false : true;
    } else {
      const newVariantIndex = clonedVariant.getVariantIndex(
        isTrackInventory,
        attributeId,
        optionId,
        onlyAllowedVariantIds
      );
      return newVariantIndex === -1 ? false : true;
    }
  }

  function renderVariants(
    variants: ProductAttributeInputModel,
    selectedVariantIndex: number,
    isTrackInventory: boolean,
    onlyAllowedVariantIds: string[],
    hasError: boolean
  ) {
    const selectedVariant =
      selectedVariantIndex > -1
        ? variants.options[selectedVariantIndex]
        : undefined;
    const extractedVariants = extractVariantOptions(
      variants,
      onlyAllowedVariantIds
    );

    return (
      <>
        {extractedVariants.map((attribute, idx) => {
          const selectedOption = selectedVariant?.attributeMappings.find(
            (a) =>
              a.attributeId === attribute.attributeId &&
              attribute.options.find((b) => b.optionId === a.optionId) !==
                undefined
          );
          return (
            <div className="full-width" key={idx}>
              <CustomLabel margin="15px 0px 15px" key={attribute.attributeId}>
                <span className="semi-bold">
                  {attribute.attributeName.toUpperCase()}:
                </span>{" "}
                {!selectedOption ? "SELECT ONE" : selectedOption?.optionName}
              </CustomLabel>
              {
                <div>
                  <FlexContainer
                    spacing={10}
                    className="attributes-alignment"
                    fxWrap="wrap"
                  >
                    {attribute.options?.map((option, index) => {
                      const isColorAttribute =
                        attribute.attributeValueType ===
                        AttributeValueTypes.Color;

                      const isSelected =
                        selectedVariant &&
                        selectedVariant.attributeMappings.find(
                          (s) =>
                            s.optionId === option.optionId &&
                            s.attributeId === attribute.attributeId
                        ) !== undefined;

                      const notAllowedClassDueToVariantIds =
                        onlyAllowedVariantIds.length > 0 &&
                        !onlyAllowedVariantIds.includes(option.variantId)
                          ? "not-allowed"
                          : "";
                      const isSelectionAllowed = isVariantSelectionAllowed(
                        variants,
                        attribute.attributeId,
                        option.optionId,
                        option.optionName,
                        isTrackInventory,
                        selectedVariantIndex,
                        onlyAllowedVariantIds
                      );
                      const showGrayedOut = !isSelectionAllowed;
                      return (
                        <SelectedColorButton
                          key={index}
                          onClick={() =>
                            !notAllowedClassDueToVariantIds &&
                            onVariantChange(
                              variants,
                              attribute.attributeId,
                              option.optionId,
                              option.optionName,
                              isTrackInventory,
                              selectedVariantIndex,
                              false,
                              onlyAllowedVariantIds
                            )
                          }
                          color={isColorAttribute ? option.color?.rgba : ""}
                          active={isSelected}
                          cursor={
                            showGrayedOut || notAllowedClassDueToVariantIds
                              ? "not-allowed"
                              : "pointer"
                          }
                          className={`color-picker-gallery ${
                            showGrayedOut
                              ? "not-allowed opaque opaque-unselected opaque-btn-as-input"
                              : "colored-btn-as-input opaque-selected"
                          }`}
                        >
                          {isColorAttribute && <div />}
                          {!isColorAttribute && <>{option.optionName}</>}
                        </SelectedColorButton>
                      );
                    })}
                  </FlexContainer>
                  {hasError && (
                    <CustomLabel color="#f00">
                      {"Please select variant"}
                    </CustomLabel>
                  )}
                </div>
              }
            </div>
          );
        })}
      </>
    );
  }

  function renderPrice() {
    if (purchaseOutDetails.activeItemIndex !== -1) {
      const addOnDetails =
        purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex];
      const addOnVariantIndex = addOnDetails.variantIndex();

      if (addOnDetails.isCustomPricing()) {
        return (
          <CustomPricingInput
            hasError={addOnDetails.hasError("customPricingInputValue")}
            value={addOnDetails.customPricingInputValue}
            onChange={(updatedValue) => onUpdateCustomPricing(updatedValue)}
            errorMessage={addOnDetails.getError("customPricingInputValue")}
          />
        );
      }
      if (
        addOnDetails.hasVariants &&
        (addOnVariantIndex === -1 || addOnVariantIndex === undefined)
      ) {
        return null;
      }
      return (
        <PurchasePriceDescriptionRow
          billingType={addOnDetails.billingType}
          terms={addOnDetails.terms}
          priceId={addOnDetails.priceId}
          recurrence={addOnDetails.recurrence}
          itemType={addOnDetails.itemType}
          hasVariants={addOnDetails.hasVariants}
          variants={
            addOnDetails.selectedVariant || new ProductAttributeInputModel()
          }
          quantity={addOnDetails.quantity}
          variantIndex={addOnVariantIndex}
          className="price-add-on add-on-no-margin"
        />
      );
    }

    if (purchaseOutDetails.item.isCustomPricing()) {
      return (
        <CustomPricingInput
          suggestedPrice={purchaseOutDetails.item.getCustomPricing()?.preset}
          hasError={purchaseOutDetails.item.hasError("customPricingInputValue")}
          value={purchaseOutDetails.item.customPricingInputValue}
          onChange={(updatedValue) => onUpdateCustomPricing(updatedValue)}
          errorMessage={purchaseOutDetails.item.getError(
            "customPricingInputValue"
          )}
        />
      );
    } else {
      if (
        purchaseOutDetails.item.selectedVariantIndex === -1 &&
        purchaseOutDetails.item.hasVariants
      )
        return null;
      return (
        <PurchasePriceDescriptionRow
          billingType={purchaseOutDetails.item.price.billingType}
          terms={purchaseOutDetails.item.price.terms}
          recurrence={purchaseOutDetails.item.price.recurrence}
          itemType={purchaseOutDetails.item.itemType}
          hasVariants={purchaseOutDetails.item.hasVariants}
          variants={purchaseOutDetails.item.variants}
          quantity={purchaseOutDetails.item.quantity}
          priceId={purchaseOutDetails.item.price.id}
          variantIndex={purchaseOutDetails.item.selectedVariantIndex}
          className="price-add-on add-on-no-margin"
        />
      );
    }
  }

  function renderActiveVariant() {
    const activeItemIndex = purchaseOutDetails.activeItemIndex;
    if (activeItemIndex === -1) {
      if (!purchaseOutDetails.item.hasVariants) {
        return null;
      }
      return renderVariants(
        variants,
        purchaseOutDetails.item.selectedVariantIndex,
        purchaseOutDetails.item.isTrackInventory,
        [],
        purchaseOutDetails.item.hasError("selectedVariantIndex")
      );
    }

    if (!purchaseOutDetails.addOns || purchaseOutDetails.addOns.length === 0) {
      return null;
    }

    const activeAddOn = purchaseOutDetails.addOns[activeItemIndex];
    const activeAddOnVariant = activeAddOn.variantIndex();
    return (
      <>
        {(activeAddOnVariant !== undefined &&
          activeAddOn.selectedVariant &&
          renderVariants(
            activeAddOn.selectedVariant,
            activeAddOnVariant,
            activeAddOn.isTrackInventory,
            activeAddOn.onlyAllowedVariantIds,
            activeAddOn.hasError("variantId")
          )) ||
          null}
      </>
    );
  }

  function renderQuantitySection() {
    const activeItemIndex = purchaseOutDetails.activeItemIndex;
    let quantity = 0;
    let hasError = false;
    if (activeItemIndex === -1) {
      quantity = purchaseOutDetails.item.quantity;
      hasError = purchaseOutDetails.item.hasError("quantity");
    } else {
      const activeAddOn = purchaseOutDetails.addOns[activeItemIndex];
      quantity = activeAddOn.quantity;
      hasError = activeAddOn.hasError("quantity");
    }

    return (
      <>
        <CustomLabel fontWeight="600" margin="15px 0px 0px">
          QUANTITY
        </CustomLabel>
        <FlexContainer
          alignItems="center"
          spacing={10}
          padding="20px 0px 0px"
          className="option-qty qty-addon-screen"
        >
          {quantity > 1 && (
            <img
              src={MinusIcon}
              alt="Delete Icon"
              onClick={() => onQuantityChange(false, quantity)}
            />
          )}
          <Input
            type="text"
            error={hasError}
            value={quantity}
            disabled={true}
            onChange={() => {}}
          />
          {/* <input type="text" value={quantity} disabled={true} /> */}
          <img
            src={Plus}
            alt="Add"
            onClick={() => onQuantityChange(true, quantity)}
          />
        </FlexContainer>
      </>
    );
  }

  function getItemName() {
    if (purchaseOutDetails.activeItemIndex === -1) {
      return purchaseOutDetails.item.name;
    } else {
      return purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex].name;
    }
  }

  function getMedia() {
    if (purchaseOutDetails.activeItemIndex === -1) {
      // const media =
      //     purchaseOutDetails.item.variants.options[purchaseOutDetails.item.selectedVariantIndex]
      //         .attributeMappings[0].medias;
      // return media.length > 0 ? media : purchaseOutDetails.item.medias;
      return purchaseOutDetails.item.getItemMedia;
    } else {
      const addOn =
        purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex];
      return addOn?.getAddOnMedia();
      // if (addOn.hasVariants) {
      //     const index = addOn.variantIndex();
      //     if (index !== undefined && index > -1 && addOn.selectedVariant) {
      //         const option = addOn.selectedVariant.options[index];
      //         return option.attributeMappings[0]?.medias || [];
      //     } else {
      //         return [];
      //     }
      // } else {
      //     return purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex].media;
      // }
    }
  }

  function mediaSection() {
    const media = getMedia();
    if (!media || media.length === 0) return null;
    return (
      <div>
        <CarousalWithThumbnail
          media={media}
          className="option-carousel-wrapper"
        />
      </div>
    );
  }

  function getItemDescription() {
    if (purchaseOutDetails.activeItemIndex === -1) {
      return purchaseOutDetails.item.descriptionEditorRawContent;
    } else {
      return purchaseOutDetails.addOns[purchaseOutDetails.activeItemIndex]
        .descriptionEditorRawContent;
    }
  }
  return (
    <FlexContainer
      className={`wrapped-columns wrapped-max-width ${!medias || medias.length === 0 ? "wrapped-width-auto" : ""}`}
      justifyContent="center"
      spacing={30}
    >
      {mediaSection()}
      <div className="options-right-panel">
        <FlexContainer fxLayout="column" spacing={15}>
          <CustomLabel className="item-header" color="#000">
            {getItemName()}
          </CustomLabel>
          {getItemDescription() !== "" && (
            <CustomLabel
              className="desc-width"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizeHtml(UtilityMethods.getEditorHtmlContent(getItemDescription())),
              }}
            />
          )}
          <div className="full-width">
            <FlexContainer fxLayout="column" spacing={0}>
              {renderPrice()}
              {reducedPriceFrom()}
            </FlexContainer>
          </div>
          {renderActiveVariant()}
          <div className="full-width">{renderQuantitySection()}</div>
          {showErrorMessage && (
            <p className="error-message">{showErrorMessage}</p>
          )}
        </FlexContainer>
      </div>
    </FlexContainer>
  );
};

export default observer(PurchaseOptions);
